import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ArrowRight } from 'lucide-react';
import { servicesSeedData } from '../database/seedData/services';

// Impact metrics removed - no fabricated data

const deliveryCadence = [
  { title: 'Discover', description: 'Mandate, use cases, data baseline, and risk posture.' },
  { title: 'Design', description: 'Architecture, operating model, controls, and value case.' },
  { title: 'Pilot', description: 'Builds, guardrails, oversight, and measurement.' },
  { title: 'Scale', description: 'MLOps, LLMOps, change plan, and governance cadence.' }
];

const Services = () => {
  const { theme } = useTheme();
  const [servicesData, setServicesData] = useState(servicesSeedData);

  useEffect(() => {
    setServicesData(servicesSeedData);
  }, []);

  // If no services are available, show a loading state
  if (!servicesData || servicesData.length === 0) {
    return (
      <div className={`container mx-auto px-4 py-12 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <h1 className={`text-4xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
          Our Services
        </h1>
        <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-center mb-12 max-w-3xl mx-auto`}>
          Loading Services...
        </p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-800'}`}>
      {/* Hero */}
      <section className={`relative overflow-hidden ${theme === 'dark' ? 'bg-slate-950' : 'bg-gradient-to-b from-white to-slate-50'} py-24 lg:py-32`}>
        <div className="relative max-w-4xl mx-auto px-6">
          <div className="space-y-8">
            <div className={`inline-block px-4 py-1.5 rounded-full ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-blue-50 border border-blue-100'}`}>
              <span className={`text-xs uppercase tracking-[0.3em] ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>Advisory Services</span>
            </div>
            <h1 className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
              Move AI from promise to performance.
            </h1>
            <p className={`text-xl lg:text-2xl leading-relaxed max-w-3xl ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>
              Three advisory lanes: AI strategy, data foundations, and production agents.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Metrics section removed - no fabricated data */}

      {/* Services Catalog */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {servicesData.map((service) => (
              <Link key={service.slug} to={`/services/${service.slug}`} className="group block">
                <div className={`relative overflow-hidden rounded-[32px] border transition-all duration-500 hover:-translate-y-2 ${
                  theme === 'dark' ? 'bg-slate-900/50 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-200 shadow-md hover:shadow-2xl'
                }`}>
                  <div className="relative p-10 h-full flex flex-col">
                    <p className={`text-xs uppercase tracking-[0.4em] mb-5 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{service.icon?.toUpperCase()}</p>
                    <h3 className={`text-2xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{service.title}</h3>
                    <p className="text-base text-slate-700 dark:text-slate-200 mb-6">{service.shortDescription}</p>
                    <div className="space-y-4 flex-1">
                      {service.keyFeatures.slice(0, 4).map((feature) => (
                        <div key={feature} className={`flex gap-3 text-sm ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}>
                          <span className="text-blue-500">•</span>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 inline-flex items-center text-sm font-semibold text-blue-500">
                      View details <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Cadence */}
      <section className={`${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-100'} py-20`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className={`text-xs uppercase tracking-[0.4em] mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Engagement Rhythm</p>
            <h2 className={`text-3xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>From Mandate To Measurable Value</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {deliveryCadence.map((phase, index) => (
              <div key={phase.title} className={`${theme === 'dark' ? 'bg-slate-950/40 border border-slate-800' : 'bg-white border border-slate-200 shadow-sm'} rounded-3xl p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs uppercase tracking-[0.3em] ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Phase</span>
                  <span className="text-2xl font-bold text-blue-500">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h3 className={`text-xl font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{phase.title}</h3>
                <p className="text-sm text-slate-700 dark:text-slate-200">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`${theme === 'dark' ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-900'} py-20`}>
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <p className="uppercase tracking-[0.4em] text-xs mb-6 text-white/60">Partner Engagement</p>
          <h2 className="text-3xl lg:text-4xl font-semibold mb-6">Need Direction For AI Investment?</h2>
          <p className="text-lg text-white/80 mb-8">We’ll map the portfolio, define ownership, and launch pilots that executives can evaluate.</p>
          <div className="flex justify-center">
            <Link to="/contact" className="px-10 py-4 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors">Schedule Working Session</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
